import {
  Box,
  Button,
  Card,
  Checkbox,
  CircularProgress,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import TrashIcon from "@mui/icons-material/Delete";
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridValueFormatterParams,
} from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useCallback, useMemo, useState } from "react";
import { enqueueSnackbar } from "notistack";

import {
  useCreateAccountHistoryMutation,
  useDeleteAccountHistoryMutation,
  useGenerateAccountHistoryFromOrionMutation,
  useGetAccountHistoryQuery,
  useSaveAllowedToHedgeMutation,
  useUpdateAccountHistoryMutation,
} from "features/trading/accounts";
import {
  NOTISTACK_FAILURE_OPTIONS,
} from "constants/index";
import {
  AccountHistoryRecord,
  AdjustmentRow,
  AdjustmentTypeEnum,
  BalanceTypeEnum,
} from "helpers/types";
import { getAdjustmentTypeColor, strategyStyles } from "styles/Strategy.styles";

import {
  AdjustmentFormSubmission,
  CreateAdjustmentModal,
} from "./CreateAdjustmentModal";

interface AdjustmentHistoryGridProps {
  accountId: number | undefined;
  strategyId: number | undefined;
  canCreate: boolean;
}

const buildAdjustmentColumns = (
  onRemove: (id: number) => void,
  onToggleIgnore: (id: number, value: boolean) => void,
): GridColDef[] => [
  {
    field: "adjustment_date",
    headerName: "Date",
    type: "string",
    minWidth: 180,
    flex: 1,
    editable: true,
    valueFormatter: ({ value }: GridValueFormatterParams) =>
      value ? dayjs(value as string).format("MM/DD/YYYY") : "N/A",
  },
  {
    field: "adjustment_type",
    headerName: "Adjustment Type",
    type: "singleSelect",
    valueOptions: [
      AdjustmentTypeEnum.INCEPTION,
      AdjustmentTypeEnum.BUY,
      AdjustmentTypeEnum.SELL,
      AdjustmentTypeEnum.REMOVED,
      AdjustmentTypeEnum.MERGE_IN,
      AdjustmentTypeEnum.JOURNAL_IN,
      AdjustmentTypeEnum.JOURNAL_OUT,
    ],
    editable: true,
    minWidth: 180,
    flex: 1,
    renderCell: (params) => {
      const adjustmentType = params.value as AdjustmentTypeEnum;
      return (
        <Typography
          color={getAdjustmentTypeColor(adjustmentType)}
          fontWeight={600}
        >
          {params.value.replace(/_/g, " ")}
        </Typography>
      );
    },
  },
  {
    field: "adjustment_amount",
    headerName: "Adjustment Amount",
    type: "number",
    editable: true,
    minWidth: 200,
    flex: 1,
  },
  {
    field: "unit_balance",
    headerName: "Unit Balance (At the moment)",
    type: "number",
    minWidth: 260,
    flex: 1.2,
  },
  {
    field: "ignore",
    headerName: "Ignore",
    minWidth: 100,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<AdjustmentRow>) => (
      <Checkbox
        checked={!!params.row.ignore}
        onChange={(event) => onToggleIgnore(params.row.id, event.target.checked)}
        onClick={(event) => event.stopPropagation()}
      />
    ),
  },
  {
    field: "actions",
    headerName: "Actions",
    minWidth: 120,
    sortable: false,
    filterable: false,
    renderCell: (params: GridRenderCellParams<AdjustmentRow>) => (
      <IconButton
        size="small"
        color="error"
        aria-label="Remove adjustment record"
        onClick={(event) => {
          event.stopPropagation();
          onRemove(params.row.id);
        }}
      >
        <TrashIcon fontSize="small" />
      </IconButton>
    ),
  },
];

const computeCurrentUnitBalanceLabel = (rows: AdjustmentRow[]): string | null => {
  const latest = rows.find((r) => !r.ignore);
  if (!latest) return null;
  if (latest.adjustment_type === AdjustmentTypeEnum.REMOVED) return "0.00";
  return Number(latest.unit_balance).toFixed(2);
};

export const AdjustmentHistoryGrid = ({
  accountId,
  strategyId,
  canCreate,
}: AdjustmentHistoryGridProps) => {
  const [balanceType, setBalanceType] = useState<BalanceTypeEnum>(
    BalanceTypeEnum.TRADE_AMOUNT,
  );
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  const { data: accountHistory = [], isLoading: isAccountHistoryLoading } =
    useGetAccountHistoryQuery(
      {
        account_id: accountId as number,
        strategy_id: strategyId as number,
        ordering: "-adjustment_date,-created_on",
        balance_type: balanceType,
      },
      { skip: !accountId || !strategyId },
    );

  const [createAccountHistory, { isLoading: isCreatingAccountHistory }] =
    useCreateAccountHistoryMutation();
  const [saveAllowedToHedge, { isLoading: isSavingAllowedToHedge }] =
    useSaveAllowedToHedgeMutation();
  const [deleteAccountHistory, { isLoading: isDeletingAccountHistory }] =
    useDeleteAccountHistoryMutation();
  const [updateAccountHistory] = useUpdateAccountHistoryMutation();
  const [generateFromOrion, { isLoading: isGenerating }] =
    useGenerateAccountHistoryFromOrionMutation();

  const adjustmentRows = useMemo<AdjustmentRow[]>(
    () =>
      accountHistory.map((item) => ({
        id: item.id,
        adjustment_date: item.adjustment_date,
        adjustment_type: item.adjustment_type as AdjustmentTypeEnum,
        adjustment_amount: item.adjustment_amount,
        unit_balance: item.unit_balance_after_adjustment,
        balance_type: item.balance_type as BalanceTypeEnum,
        ignore: item.ignore,
      })),
    [accountHistory],
  );

  const hasAccountHistory = adjustmentRows.length > 0;
  const currentUnitBalanceLabel = useMemo(
    () => computeCurrentUnitBalanceLabel(adjustmentRows),
    [adjustmentRows],
  );

  const handleRemoveAdjustmentRecord = useCallback(
    (id: number) => deleteAccountHistory({ id }),
    [deleteAccountHistory],
  );

  const handleToggleIgnore = useCallback(
    (id: number, value: boolean) => updateAccountHistory({ id, ignore: value }),
    [updateAccountHistory],
  );

  const handleProcessRowUpdate = useCallback(
    (newRow: AdjustmentRow, oldRow: AdjustmentRow): AdjustmentRow => {
      const patch: Partial<AccountHistoryRecord> & { id: number } = {
        id: newRow.id,
      };
      let changed = false;

      if (
        newRow.adjustment_date !== oldRow.adjustment_date &&
        newRow.adjustment_date
      ) {
        patch.adjustment_date = newRow.adjustment_date;
        changed = true;
      }
      if (newRow.adjustment_type !== oldRow.adjustment_type) {
        patch.adjustment_type = newRow.adjustment_type;
        changed = true;
      }
      if (
        newRow.adjustment_amount !== oldRow.adjustment_amount &&
        newRow.adjustment_amount !== null &&
        !Number.isNaN(newRow.adjustment_amount)
      ) {
        patch.adjustment_amount = Number(newRow.adjustment_amount);
        changed = true;
      }

      if (changed) updateAccountHistory(patch);
      return newRow;
    },
    [updateAccountHistory],
  );

  const adjustmentColumns = useMemo(
    () => buildAdjustmentColumns(handleRemoveAdjustmentRecord, handleToggleIgnore),
    [handleRemoveAdjustmentRecord, handleToggleIgnore],
  );

  const handleSubmitNewAdjustment = async (values: AdjustmentFormSubmission) => {
    if (!accountId || !strategyId) return;
    await createAccountHistory({
      account: accountId,
      strategy: strategyId,
      adjustment_date: values.adjustment_date,
      adjustment_type: values.adjustment_type,
      adjustment_amount: values.adjustment_amount,
      balance_type: balanceType,
      ignore: values.ignore,
    }).unwrap();
    setIsCreateModalOpen(false);
  };

  const handleAllowedToHedge = useCallback(async () => {
    if (!accountId || !strategyId) return;
    try {
      await saveAllowedToHedge({
        account_id: accountId,
        strategy_id: strategyId,
      }).unwrap();
    } catch {
      enqueueSnackbar(
        "Couldn't create trade amount history for this account.",
        NOTISTACK_FAILURE_OPTIONS,
      );
    }
  }, [accountId, strategyId, saveAllowedToHedge]);

  const handleGenerateFromOrion = useCallback(async () => {
    if (!accountId || !strategyId) return;
    try {
      await generateFromOrion({
        account_id: accountId,
        strategy_id: strategyId,
      }).unwrap();
    } catch {
      enqueueSnackbar(
        "Couldn't generate portfolio history for this account.",
        NOTISTACK_FAILURE_OPTIONS,
      );
    }
  }, [accountId, strategyId, generateFromOrion]);


  return (
    <Card sx={{ padding: 2 }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        marginBottom={2}
      >
        <Typography variant="h6" color="primary.main">
          {hasAccountHistory ? (
            <>
              Current amount of shares for the account:{" "}
              <Typography component="span" color="text.primary">
                {currentUnitBalanceLabel}
              </Typography>
            </>
          ) : (
            "Please set Inception date and amount"
          )}
        </Typography>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={balanceType}
          onChange={(_, value) => {
            if (value !== null) setBalanceType(value);
          }}
        >
          <ToggleButton value={BalanceTypeEnum.TRADE_AMOUNT}>
            Trade Amount
          </ToggleButton>
          <ToggleButton value={BalanceTypeEnum.PORTFOLIO}>Portfolio</ToggleButton>
        </ToggleButtonGroup>
        <Box display="flex" gap={1}>
          {balanceType === BalanceTypeEnum.TRADE_AMOUNT && (
              <span>
                <Button
                  variant="outlined"
                  disabled={
                    !canCreate || hasAccountHistory || isSavingAllowedToHedge
                  }
                  onClick={handleAllowedToHedge}
                >
                  Allowed to Hedge
                </Button>
              </span>
          )}
          {balanceType === BalanceTypeEnum.PORTFOLIO && (
            <Tooltip title={hasAccountHistory ? "Portfolio history already exists" : ""}>
              <span>
                <Button
                  variant="outlined"
                  disabled={!canCreate || hasAccountHistory || isGenerating}
                  onClick={handleGenerateFromOrion}
                  startIcon={isGenerating ? <CircularProgress size={16} /> : null}
                >
                  {isGenerating ? "Generating…" : "Generate history"}
                </Button>
              </span>
            </Tooltip>
          )}
          <Button
            variant="contained"
            onClick={() => setIsCreateModalOpen(true)}
            disabled={!canCreate}
          >
            Adjust amount
          </Button>
        </Box>
      </Box>
      <DataGrid
        hideFooter
        columns={adjustmentColumns}
        rows={adjustmentRows}
        loading={isAccountHistoryLoading || isDeletingAccountHistory}
        getRowHeight={() => "auto"}
        sx={strategyStyles.adjustmentDataGrid}
        processRowUpdate={handleProcessRowUpdate}
      />
      <CreateAdjustmentModal
        open={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleSubmitNewAdjustment}
        hasExistingHistory={hasAccountHistory}
        balanceType={balanceType}
        submitting={isCreatingAccountHistory}
      />
    </Card>
  );
};
